import * as z from "zod";
import { userFacingError } from "../authorization";
import { zql } from "../generatedDrizzleSchema";
import { creatorColumns } from "../rbac/roles";
import { readSkillFiles, replaceSkillFiles, writeSkillMd } from "../skillFiles";
import { hashSkillBundle, type SkillBundle } from "../skillGitUpdates";
import { skills as builtinSkills } from "../skills";
import { skillNameError } from "../skills/spec";
import { mutator } from "./mutator";

const checkSkillUpdatesSchema = z.object({
  skillId: z.string(),
  connectedAppId: z.string().nullable(),
});
const configureSkillUpdatesSchema = checkSkillUpdatesSchema.extend({
  automatic: z.boolean(),
});
const applySkillUpdateSchema = checkSkillUpdatesSchema.extend({
  candidateHash: z.string(),
  candidateCommit: z.string(),
  installedHash: z.string(),
});
const resolve = { skill: { kind: "skill", from: "skillId" } } as const;
const permissions = [
  {
    permission: "skill:write",
    scopeId: ({ args }: { args: { skill: { id: string } } }) => args.skill.id,
  },
  {
    permission: "connected_app:use",
    scopeId: ({ args }: { args: { connectedAppId: string | null } }) =>
      args.connectedAppId,
  },
] as const;

export const checkSkillUpdates = mutator({
  schema: checkSkillUpdatesSchema,
  resolve,
  permissions,
  fn: async ({ tx, ctx, args: { skill, connectedAppId } }) => {
    const installation = await tx.run(
      zql.skillGitInstallations
        .where("skillId", skill.id)
        .related("source")
        .one()
    );
    if (!installation?.source) throw userFacingError("Git source not found");
    if (installation.source.connectedAppId !== connectedAppId)
      throw userFacingError("Git connector does not match this skill");
    const creator = creatorColumns(ctx);
    await tx.mutate.skillGitInstallations.update({
      skillId: skill.id,
      lastError: null,
      authorizedByUserId: creator.createdByUserId,
      authorizedByServiceAccountId: creator.createdByServiceAccountId,
    });
    if (!installation.source.checkExecutionId)
      await tx.mutate.skillGitSources.update({
        id: installation.sourceId,
        nextCheckAt: 0,
        checkRequestedAt: Date.now(),
        checkError: null,
      });
  },
});

export const configureSkillUpdates = mutator({
  schema: configureSkillUpdatesSchema,
  resolve,
  permissions: [
    permissions[0],
    {
      permission: "connected_app:use",
      scopeId: ({ args }) => (args.automatic ? args.connectedAppId : null),
    },
  ],
  fn: async ({ tx, ctx, args }) => {
    const installation = await tx.run(
      zql.skillGitInstallations
        .where("skillId", args.skill.id)
        .related("source")
        .one()
    );
    if (!installation?.source) throw userFacingError("Git source not found");
    if (installation.source.connectedAppId !== args.connectedAppId)
      throw userFacingError("Git connector does not match this skill");
    if (args.automatic && installation.syncStartedAt !== null)
      throw userFacingError("Wait for the current sync to finish");
    const creator = creatorColumns(ctx);
    await tx.mutate.skillGitInstallations.update({
      skillId: args.skill.id,
      autoUpdate: false,
      autoUpdateRequested: args.automatic,
      updateRequestedAt: args.automatic ? Date.now() : null,
      ...(args.automatic
        ? {
            authorizedByUserId: creator.createdByUserId,
            authorizedByServiceAccountId: creator.createdByServiceAccountId,
          }
        : {}),
      lastError: null,
    });
    if (args.automatic)
      await tx.mutate.skillGitSources.update({
        id: installation.sourceId,
        nextCheckAt: 0,
        checkRequestedAt: Date.now(),
        checkError: null,
      });
  },
});

export const applySkillUpdate = mutator({
  schema: applySkillUpdateSchema,
  resolve,
  permissions,
  fn: async ({ tx, ctx, args }) => {
    const installation = await tx.run(
      zql.skillGitInstallations
        .where("skillId", args.skill.id)
        .related("source")
        .related("candidateBlob")
        .one()
    );
    if (
      !installation?.source ||
      installation.source.connectedAppId !== args.connectedAppId
    )
      throw userFacingError("Git connector does not match this skill");
    if (
      installation.autoUpdate ||
      installation.autoUpdateRequested ||
      installation.syncStartedAt !== null
    )
      throw userFacingError("Wait for the current update to finish");
    if (
      !installation.candidateBlob ||
      installation.candidateHash !== args.candidateHash ||
      installation.candidateCommit !== args.candidateCommit
    )
      throw userFacingError("The available update changed. Review it again");
    if (
      (await hashSkillBundle(await readSkillFiles(tx, args.skill.id))).hash !==
      args.installedHash
    )
      throw userFacingError(
        "The installed files changed. Review the update again"
      );
    const files: SkillBundle = JSON.parse(installation.candidateBlob.content);
    await replaceSkillFiles(tx, args.skill.id, ctx.tenantId, files);
    const creator = creatorColumns(ctx);
    await tx.mutate.skillGitInstallations.update({
      skillId: args.skill.id,
      authorizedByUserId: creator.createdByUserId,
      authorizedByServiceAccountId: creator.createdByServiceAccountId,
      syncedContentHash: args.candidateHash,
      lastSyncedCommit: installation.candidateCommit,
      lastSyncedAt: Date.now(),
      candidateHash: null,
      candidateCommit: null,
      baselineUnavailable: false,
      lastError: null,
    });
  },
});

const updateSkillSettingsSchema = z.object({
  skillId: z.string(),
  enabled: z.boolean().optional(),
  required: z.boolean().optional(),
  name: z.string().optional(),
});
export const updateSkillSettings = mutator({
  schema: updateSkillSettingsSchema,
  resolve,
  permissions: [
    { permission: "skill:write", scopeId: ({ args }) => args.skill.id },
  ],
  fn: async ({ tx, ctx, args: { skill, enabled, required, name } }) => {
    if (name !== undefined) {
      const trimmed = name.trim();
      const error = skillNameError(trimmed);
      if (error) throw userFacingError(error);
      const existing = await tx.run(
        zql.skills.where("name", trimmed).where("id", "!=", skill.id).one()
      );
      if (existing || builtinSkills.some((skill) => skill.name === trimmed))
        throw userFacingError("A skill with this name already exists");
      const current = await tx.run(zql.skills.where("id", skill.id).one());
      if (!current) throw userFacingError("Skill not found");
      await writeSkillMd(tx, skill.id, ctx.tenantId, {
        name: trimmed,
        description: current.description,
        instructions: current.instructions,
      });
      await tx.mutate.skills.update({ id: skill.id, name: trimmed });
    }
    if (enabled !== undefined)
      await tx.mutate.skills.update({ id: skill.id, enabled });
    if (required !== undefined)
      await tx.mutate.skills.update({ id: skill.id, required });
  },
});
